<template>
	<div ref="root" class="row-hold-menu">
		<button
			type="button"
			class="row-hold-menu-trigger"
			:title="label"
			:aria-label="label"
			aria-haspopup="menu"
			:aria-expanded="open ? 'true' : 'false'"
			:disabled="disabled"
			@click.stop="open = !open"
		>
			<AppointmentIcon name="more" />
		</button>
		<div v-if="open" class="row-hold-menu-panel" v-floating-menu @click.stop>
			<HoldToConfirm class="row-hold-menu-action" @confirm="confirm" @cancel="close">
				<slot />
			</HoldToConfirm>
		</div>
	</div>
</template>

<script setup>
import { onBeforeUnmount, onDeactivated, ref, watch } from 'vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import HoldToConfirm from '@/components/HoldToConfirm.vue';
import vFloatingMenu from '@/directives/floatingMenu';

// A row's ⋯ menu with one destructive action that has to be held, as in the journals.
defineProps({
	label: { type: String, default: 'More options' },
	disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['confirm']);

const root = ref(null);
const open = ref(false);

const close = () => {
	open.value = false;
};

const confirm = () => {
	close();
	emit('confirm');
};

const handleOutsidePointer = (event) => {
	if (!root.value?.contains(event.target)) close();
};

watch(open, (isOpen) => {
	if (isOpen) document.addEventListener('pointerdown', handleOutsidePointer, true);
	else document.removeEventListener('pointerdown', handleOutsidePointer, true);
});

onDeactivated(close);
onBeforeUnmount(() => {
	document.removeEventListener('pointerdown', handleOutsidePointer, true);
});
</script>

<style scoped>
.row-hold-menu {
	position: relative;
	display: inline-flex;
}

.row-hold-menu-trigger {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	padding: 0;
	border: 0;
	border-radius: 6px;
	background: transparent;
	color: var(--text-color-secondary);
	cursor: pointer;
	transition: background-color 0.15s ease;
}

.row-hold-menu-trigger:hover:not(:disabled),
.row-hold-menu-trigger[aria-expanded='true'] {
	background-color: color-mix(in srgb, var(--text-color-secondary) 6%, transparent);
}

.row-hold-menu-trigger:disabled {
	opacity: 0.4;
	cursor: default;
}

.row-hold-menu-trigger:focus-visible {
	outline: 2px solid var(--noc-blue);
	outline-offset: 2px;
}

/* Opens like the journal row menus. */
.row-hold-menu-panel {
	position: absolute;
	top: 100%;
	right: 0;
	z-index: 40;
	min-width: 150px;
	padding: 4px 0;
	border: 1px solid var(--border-color, #dcdcdc);
	border-radius: 4px;
	background-color: var(--noc-background, #fff);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
	animation: row-hold-menu-enter 160ms ease-out both;
}

.row-hold-menu-action {
	display: block;
	width: 100%;
	padding: 8px 16px;
	border: none;
	background: none;
	color: var(--noc-red, #c45151);
	font: var(--ap-type-control, 500 0.875rem/1.25rem var(--font-family-base));
	text-align: left;
	white-space: nowrap;
	cursor: pointer;
}

.row-hold-menu-action:hover:not(:disabled) {
	background-color: var(--noc-red-10, rgba(196, 81, 81, 0.1));
}

@keyframes row-hold-menu-enter {
	from {
		opacity: 0;
		transform: translateY(-4px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@media (prefers-reduced-motion: reduce) {
	.row-hold-menu-panel {
		animation: none;
	}
}
</style>
